import React, { useState } from 'react'
import { Dialog, Modal } from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@tanstack/react-query'

import { fetchAvailableUnits, saveIngredientUnits } from '@/api'
import { IngredientUnit, TempIngredientUnit } from '@/api/types'
import { Button } from '@/components/Button'
import { ModalContent, ModalFooter, ModalHeader } from '@/components/Modal'
import { TextField } from '@/components/TextField'
import { Select } from '@/components/Select'
import { Spinner } from '@/components/Spinner'
import { Label } from '@/components/Label'

import './NewServingSizeModal.css'

type OptionType = {
  value: any
  label: any
}

type MeasurementUnit = TempIngredientUnit | IngredientUnit

type Props = {
  isOpen: boolean
  setOpen: (state: boolean) => void
  units: IngredientUnit[] | undefined
  idIngredient: number
  onUpdateUnit: (unit: IngredientUnit) => void
  onUpdateUnits: (units: IngredientUnit[]) => void
}

export default function NewServingSizeModal({ isOpen, setOpen, ...props }: Props) {
  const { t } = useTranslation()

  return (
    <Modal
      style={{ width: 300, minHeight: 'auto' }}
      isDismissable
      isOpen={isOpen}
      onOpenChange={setOpen}>
      <Dialog aria-label="Units dialog">
        {({ close }) => (
          <>
            <ModalHeader onClose={close}>{t('ingredient.newUnit')}</ModalHeader>

            <NewServingSizeContent {...props} close={close} />
          </>
        )}
      </Dialog>
    </Modal>
  )
}

type ContentProps = {
  close: () => void
} & Omit<Props, 'isOpen' | 'setOpen'>

function NewServingSizeContent({
  units = [],
  idIngredient,
  onUpdateUnits,
  onUpdateUnit,
  close,
}: ContentProps) {
  const { t } = useTranslation()
  const [quantity, setQuantity] = useState(1)
  const [unit, setUnit] = useState<OptionType>()

  const { data, isLoading } = useQuery({
    queryKey: ['availableUnits'],
    queryFn: fetchAvailableUnits,
    staleTime: Infinity,
  })

  const saveMutation = useMutation({
    mutationFn: (updatedUnits: MeasurementUnit[]) =>
      saveIngredientUnits(idIngredient, updatedUnits),
    onSuccess: (res: IngredientUnit[]) => {
      onUpdateUnits(res)
      onUpdateUnit(res[res.length - 1])
      close()
    },
  })

  const handleUpdateUnit = (unit: OptionType) => {
    setUnit(unit)
  }

  const handleSave = () => {
    const newUnit = {
      name: unit?.value,
      quantityInGrams: quantity,
    } as TempIngredientUnit

    saveMutation.mutate([...units, newUnit])
  }

  if (isLoading) {
    return <Spinner />
  }

  if (data === undefined) {
    return null
  }

  const unitsOptions = data.map((unit) => ({
    value: unit,
    label: t(`measurementUnits.${unit}` as any),
  }))

  return (
    <>
      <ModalContent style={{ overflow: 'visible' }}>
        <div className="NewServingSizeModal-content">
          <div>
            <Label>{t('recipe.unit')}</Label>
            <Select
              options={unitsOptions}
              value={unit}
              onChange={handleUpdateUnit}
              getOptionValue={(option) => option.value}
              getOptionLabel={(option) => option.label}
              isOptionDisabled={(item) => {
                return units.some((unit) => unit.name === item.value)
              }}
              components={{
                IndicatorSeparator: null,
              }}
            />
          </div>

          <TextField
            isRequired
            label={t('ingredient.grams')}
            value={quantity.toString()}
            errorMessage={t('recipe.errorNameEmpty')}
            onChange={(value) => setQuantity(parseInt(value))}
          />
        </div>
      </ModalContent>
      <ModalFooter onClose={close}>
        <Button variant="primary" onPress={handleSave}>
          {t('ingredient.createUnit')}
        </Button>
      </ModalFooter>
    </>
  )
}
